// Per-page document title + meta description.
//
// index.html ships a static `<title>Folio</title>` and a generic
// `<meta name="description">` as the fallback. Each page calls
// `setPageTitle()` from its `connectedCallback` (static section pages) or
// when the deck loads (studio / viewer / share) so the browser tab and
// link-preview metadata track the current route. There is no router hook —
// a tiny shared helper keeps the logic in one place without coupling the
// pages to the router.

/** The product name appended to every page title. */
const SUFFIX = 'Folio';

/**
 * Set the document title and (optionally) the meta description.
 *
 * Pass the bare page name — the helper appends " · Folio". An empty / falsy
 * `title` falls back to the plain product name, matching index.html's
 * default so a half-loaded page never shows a blank tab.
 *
 * @param title  Page name, e.g. "Decks" or a deck's own title. Falsy → "Folio".
 * @param description  Optional meta-description override for the section.
 */
export function setPageTitle(title?: string, description?: string): void {
  const clean = (title ?? '').trim();
  document.title = clean ? `${clean} · ${SUFFIX}` : SUFFIX;

  if (description != null) {
    setMetaDescription(description);
  }
}

/**
 * Build a "<deck title> · Folio" document title for the deck surfaces
 * (studio, viewer, share). A missing / blank deck title falls back to
 * "Untitled deck"; pass `loaded = false` while the deck is still being
 * fetched to keep the plain "Folio" tab until the title is known.
 */
export function setDeckTitle(deckTitle: string | undefined, loaded = true): void {
  if (!loaded) {
    setPageTitle();
    return;
  }
  setPageTitle((deckTitle ?? '').trim() || 'Untitled deck');
}

/** Update the `<meta name="description">` content, if the tag exists. */
function setMetaDescription(content: string): void {
  const meta = document.querySelector('meta[name="description"]');
  if (meta) meta.setAttribute('content', content);
}
